import clsx from 'clsx'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import Text from '@/components-latest/primitives/Text'
import { SupportedLanguages } from '@/config/i18n'
import { IBillings, OrderStatuses } from '@/ducks/orders/orders.types'
import { selectCurrencyCode } from '@/ducks/store/selectors/store.selectors'
import { formatPrice } from '@/utils/currency.utils'

import Styles from './PaymentSummary.module.scss'
interface IPaymentSummaryProps {
  billing: IBillings
  status: OrderStatuses
  className?: string
  isEpay?: boolean
}

const PaymentSummary: React.FC<IPaymentSummaryProps> = ({
  className,
  billing: {
    subtotal,
    discount,
    earnings,
    commission,
    paymentMethod: { paymentMethodDisplayName_i18n }
  },
  status,
  isEpay
}) => {
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language as SupportedLanguages
  const currency = useSelector(selectCurrencyCode) || ''

  const shouldShowEpaymentInfo =
    isEpay && ['ONGOING', 'READY', 'ACCEPTED'].includes(status)

  return (
    <div
      data-testid="payment-summary"
      className={clsx(Styles.paymentSummary, className)}>
      <div className={Styles.summaryRow}>
        <Text variant="nextSubtitle">{t('orders.subtotal')}</Text>
        <div className={Styles.dots} />
        <Text variant="nextSubtitle" weight="bold">
          {formatPrice(subtotal, currency, { signDisplay: 'never' })}
        </Text>
      </div>

      <div className={Styles.summaryRow}>
        <Text variant="nextSubtitle">{t('orders.discount')}</Text>
        <div className={Styles.dots} />
        <Text variant="nextSubtitle" weight="bold">
          {formatPrice(discount, currency, { signDisplay: 'never' })}
        </Text>
      </div>

      <div className={Styles.summaryRow}>
        <Text variant="nextSubtitle">{t('orders.commission')}</Text>
        <div className={Styles.dots} />
        <Text variant="nextSubtitle" weight="bold">
          {formatPrice(commission, currency, { signDisplay: 'never' })}
        </Text>
      </div>

      <div className={Styles.summaryRow}>
        <Text variant="nextSubtitle">{t('orders.earnings')}</Text>
        <div className={Styles.dots} />
        <Text variant="nextSubtitle" weight="bold" className={Styles.positive}>
          {formatPrice(earnings, currency, { signDisplay: 'never' })}
        </Text>
      </div>

      <div className={Styles.summaryRow}>
        <Text variant="nextSubtitle">{t('orders.payment-method')}</Text>
        <div className={Styles.dots} />
        <Text variant="nextSubtitle" weight="bold">
          {paymentMethodDisplayName_i18n?.[currentLanguage]}
        </Text>
      </div>

      {shouldShowEpaymentInfo && (
        <Text variant="nextSubtitle" weight="bold">
          {t('orders.epayment-info')}
        </Text>
      )}
    </div>
  )
}

export default PaymentSummary
